import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Chip, CircularProgress, Tabs, Tab,
  Card, CardContent, Avatar, Alert,
} from '@mui/material';
import { loadBase, loadPlayerStats } from '../../services/espnService';
import { useConfig } from '../../hooks/useConfig';

// ── Sleeper API ────────────────────────────────────────────────────────────────
const SLEEPER_TRENDING_URL = (type, hours = 168, limit = 200) =>
  `https://api.sleeper.app/v1/players/nfl/trending/${type}?lookback_hours=${hours}&limit=${limit}`;

let _sleeperPlayersCache = null;
async function loadSleeperPlayers() {
  if (_sleeperPlayersCache) return _sleeperPlayersCache;
  const res = await fetch('https://api.sleeper.app/v1/players/nfl');
  _sleeperPlayersCache = await res.json();
  return _sleeperPlayersCache;
}

async function loadTrending(type = 'add') {
  const res = await fetch(SLEEPER_TRENDING_URL(type, 168, 200));
  return res.json(); // [{ player_id, count }]
}

// ── Name-based fuzzy join ──────────────────────────────────────────────────────
// Normalize names for matching (strip punctuation, lowercase)
function normalizeName(name = '') {
  return name.toLowerCase()
    .replace(/['.,-]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Build a lookup map: normalized name + pos → ESPN player
function buildEspnNameMap(players) {
  const map = {};
  for (const p of Object.values(players)) {
    const key = `${normalizeName(p.name)}_${p.posKey}`;
    map[key] = p;
  }
  return map;
}

// Join Sleeper player → ESPN player via:
// 1. Direct espn_id (fast path)
// 2. Fuzzy name + position match (fallback)
function joinSleeperToEspn(sleeperPlayer, espnNameMap, espnById) {
  // Fast path — espn_id available
  const espnId = sleeperPlayer.espn_id;
  if (espnId && espnById[String(espnId)]) return espnById[String(espnId)];

  // Fuzzy name match
  const pos = sleeperPlayer.position;
  const espnPos = { WR: 'WR', RB: 'RB', QB: 'QB', TE: 'TE', K: 'K' }[pos];
  if (!espnPos || !sleeperPlayer.full_name) return null;

  const normKey = `${normalizeName(sleeperPlayer.full_name)}_${espnPos}`;
  return espnNameMap[normKey] || null;
}

// ── Scoring ────────────────────────────────────────────────────────────────────
// Waiver score: projection + NR bonus + trending boost
// All inputs are normalized before combining.

function computeWaiverScore(player, stats, trendCount, trendMax) {
  const s = stats[player.id] || {};
  const gp = s.gamesPlayed || 1;

  // Projected pts/game proxy: fantasy_points_ppr from adv stats OR raw PPR calc
  const proj = s.fantasyPtsG ?? (s.fantasy_points_ppr ? s.fantasy_points_ppr / gp : 0);

  // NR rank (lower = better) → normalize to 0-1 bonus
  const nrRank = player.nr ?? 99;
  const nrBonus = Math.max(0, (32 - nrRank) / 32) * 5; // max +5

  // Sleeper trending add count → normalized 0-1 * 10
  const trendBonus = trendMax > 0 ? (trendCount / trendMax) * 10 : 0;

  return proj + nrBonus + trendBonus;
}

// Generate a one-line "why" blurb
function generateWhy(player, stats, trendCount, trendMax) {
  const s = stats[player.id] || {};
  const gp = s.gamesPlayed || 1;
  const nrRank = player.nr ?? 99;
  const trendPct = trendMax > 0 ? Math.round((trendCount / trendMax) * 100) : 0;

  const parts = [];

  if (nrRank <= 5) parts.push(`Elite NR rank (#${nrRank})`);
  else if (nrRank <= 12) parts.push(`Strong NR rank (#${nrRank})`);
  else if (nrRank <= 24) parts.push(`Solid NR rank (#${nrRank})`);

  if (trendPct >= 80) parts.push('🔥 trending massively on Sleeper');
  else if (trendPct >= 50) parts.push(`trending hard on Sleeper (+${trendCount.toLocaleString()} adds)`);
  else if (trendPct >= 20) parts.push(`picking up adds on Sleeper`);

  const recYpg = gp > 0 && s.receivingYards ? (s.receivingYards / gp).toFixed(0) : null;
  const rushYpg = gp > 0 && s.rushingYards ? (s.rushingYards / gp).toFixed(0) : null;
  if (recYpg > 70) parts.push(`${recYpg} rec yds/g`);
  else if (rushYpg > 65) parts.push(`${rushYpg} rush yds/g`);

  if (parts.length === 0) return 'Available in most leagues, worth a look.';
  return parts.join(' · ');
}

// ── Positions to include ───────────────────────────────────────────────────────
const WAIVER_POSITIONS = ['QB', 'RB', 'WR', 'TE'];
const POSITION_TABS = ['All', ...WAIVER_POSITIONS];

// ── Color helpers ─────────────────────────────────────────────────────────────
const POS_COLORS = { QB: '#f5a623', RB: '#2ea84b', WR: '#38bdf8', TE: '#a78bfa' };
const trendBadge = (count, max) => {
  const pct = max > 0 ? count / max : 0;
  if (pct >= 0.8) return { label: '🔥 Hot', color: '#ef4444', bg: 'rgba(239,68,68,0.15)' };
  if (pct >= 0.5) return { label: '↑ Trending', color: '#f5a623', bg: 'rgba(245,166,35,0.12)' };
  if (pct >= 0.2) return { label: '↗ Rising', color: '#2ea84b', bg: 'rgba(46,168,75,0.12)' };
  return { label: 'Available', color: '#6a8aa8', bg: 'rgba(106,138,168,0.08)' };
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function WaiverWire() {
  useConfig(); // ensure config cache is primed
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [posTab, setPosTab] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      loadBase(),
      loadSleeperPlayers(),
      loadTrending('add'),
    ]).then(async ([{ players }, sleeperPlayers, trendingAdds]) => {
      if (cancelled) return;

      // Load player stats for all offensive skill players
      const skillIds = Object.values(players)
        .filter(p => WAIVER_POSITIONS.includes(p.posKey))
        .map(p => p.id);

      const stats = await loadPlayerStats(skillIds);
      if (cancelled) return;

      // Build lookup maps
      const espnNameMap = buildEspnNameMap(players);
      const espnById = {};
      for (const p of Object.values(players)) espnById[p.id] = p;

      // Build Sleeper trending map: sleeperPlayerId → count
      const trendMap = {};
      let trendMax = 0;
      for (const item of trendingAdds) {
        trendMap[item.player_id] = item.count;
        if (item.count > trendMax) trendMax = item.count;
      }

      // For each trending player, attempt join to ESPN
      const joined = [];
      const seenEspnIds = new Set();
      for (const item of trendingAdds) {
        const sp = sleeperPlayers[item.player_id];
        if (!sp || !WAIVER_POSITIONS.includes(sp.position)) continue;
        const espnPlayer = joinSleeperToEspn(sp, espnNameMap, espnById);
        if (!espnPlayer) continue;
        if (seenEspnIds.has(espnPlayer.id)) continue;
        seenEspnIds.add(espnPlayer.id);
        joined.push({
          espnPlayer,
          sleeperPlayer: sp,
          trendCount: item.count,
          trendMax,
        });
      }

      // Also add skill players with strong projections who aren't already in list
      // (ensures we surface NR-based pickups even without Sleeper data)
      for (const p of Object.values(players)) {
        if (!WAIVER_POSITIONS.includes(p.posKey)) continue;
        if (seenEspnIds.has(p.id)) continue;
        const s = stats[p.id];
        if (!s || (s.gamesPlayed || 0) < 4) continue;
        const proj = s.fantasyPtsG ?? 0;
        if (proj < 8) continue; // minimum threshold
        joined.push({ espnPlayer: p, sleeperPlayer: null, trendCount: 0, trendMax });
        seenEspnIds.add(p.id);
      }

      // Score and sort
      const scored = joined.map(({ espnPlayer, sleeperPlayer, trendCount, trendMax: tm }) => {
        const wScore = computeWaiverScore(espnPlayer, stats, trendCount, tm);
        const why = generateWhy(espnPlayer, stats, trendCount, tm);
        const s = stats[espnPlayer.id] || {};
        const gp = s.gamesPlayed || 1;
        return {
          ...espnPlayer,
          trendCount,
          trendMax: tm,
          waiverScore: wScore,
          why,
          proj: s.fantasyPtsG ?? (s.fantasy_points_ppr ? +(s.fantasy_points_ppr / gp).toFixed(1) : 0),
          nr: espnPlayer.nr ?? null,
        };
      }).sort((a, b) => b.waiverScore - a.waiverScore).slice(0, 50);

      setResults(scored);
      setLoading(false);
    }).catch(e => {
      if (!cancelled) { setError(e.message || 'Failed to load waiver data'); setLoading(false); }
    });

    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const pos = POSITION_TABS[posTab];
    return pos === 'All' ? results : results.filter(p => p.posKey === pos);
  }, [results, posTab]);

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
      <CircularProgress size={36} sx={{ color: '#f5a623' }} />
      <Typography variant="body2" sx={{ color: '#9eb8d0' }}>Loading waiver targets…</Typography>
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 3 }}>
      <Alert severity="error" sx={{ fontSize: '0.8rem' }}>{error}</Alert>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#e8f0f8' }}>
          🔄 Waiver Wire Targets
        </Typography>
        <Chip label="Nerds+" color="secondary" size="small" sx={{ fontWeight: 800, fontSize: '0.65rem', height: 20 }} />
      </Box>
      <Typography sx={{ fontSize: '0.75rem', color: '#4a6080', mb: 2 }}>
        Ranked by projection + NR score + Sleeper trending adds (7-day). Updated daily.
      </Typography>

      {/* Position filter */}
      <Tabs
        value={posTab}
        onChange={(_, v) => setPosTab(v)}
        sx={{
          mb: 2.5,
          minHeight: 36,
          '& .MuiTabs-indicator': { backgroundColor: '#f5a623' },
          '& .MuiTab-root': { minHeight: 36, py: 0, px: 1.5, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', color: '#6a8aa8', '&.Mui-selected': { color: '#f5a623' } },
        }}
      >
        {POSITION_TABS.map(p => <Tab key={p} label={p} />)}
      </Tabs>

      {/* Player cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {filtered.slice(0, 25).map((player, idx) => {
          const posColor = POS_COLORS[player.posKey] || '#9eb8d0';
          const badge = trendBadge(player.trendCount, player.trendMax);

          return (
            <Card key={player.id} sx={{
              backgroundColor: 'rgba(10,20,34,0.85)',
              border: '1px solid rgba(30,52,72,0.6)',
              borderLeft: `3px solid ${posColor}55`,
              borderRadius: 2,
              transition: 'border-color 0.15s',
              '&:hover': { borderColor: posColor + '66', borderLeft: `3px solid ${posColor}` },
            }}>
              <CardContent sx={{ p: '12px 16px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {/* Rank */}
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#f5a623', width: 20, textAlign: 'right', flexShrink: 0 }}>
                    {idx + 1}
                  </Typography>

                  {/* Avatar */}
                  <Avatar sx={{ width: 34, height: 34, bgcolor: posColor + '22', color: posColor, fontSize: '0.65rem', fontWeight: 700, flexShrink: 0 }}>
                    {player.posKey}
                  </Avatar>

                  {/* Name + team + why */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#e8f0f8', lineHeight: 1.2 }}>
                        {player.name}
                      </Typography>
                      <Chip label={player.team || '—'} size="small"
                        sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, color: '#9eb8d0', bgcolor: 'rgba(30,52,72,0.6)', px: 0.5 }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.68rem', color: '#4a6080', mt: 0.3, lineHeight: 1.4 }}>
                      {player.why}
                    </Typography>
                  </Box>

                  {/* Stats column */}
                  <Box sx={{ display: 'flex', gap: 2, flexShrink: 0, alignItems: 'center' }}>
                    {/* Trend badge */}
                    {player.trendCount > 0 && (
                      <Box sx={{ textAlign: 'center' }}>
                        <Chip label={badge.label} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, color: badge.color, bgcolor: badge.bg, mb: 0.25 }} />
                        <Typography sx={{ fontSize: '0.6rem', color: '#4a6080', display: 'block' }}>
                          +{player.trendCount.toLocaleString()} adds
                        </Typography>
                      </Box>
                    )}

                    {/* Projection */}
                    <Box sx={{ textAlign: 'center', minWidth: 48 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#2ea84b', lineHeight: 1 }}>
                        {player.proj > 0 ? player.proj.toFixed(1) : '—'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#4a6080' }}>proj pts</Typography>
                    </Box>

                    {/* NR rank */}
                    {player.nr && (
                      <Box sx={{ textAlign: 'center', minWidth: 36 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#f5a623', lineHeight: 1 }}>
                          #{player.nr}
                        </Typography>
                        <Typography sx={{ fontSize: '0.6rem', color: '#4a6080' }}>NR</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {filtered.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography sx={{ color: '#4a6080', fontSize: '0.85rem' }}>No players found for this position.</Typography>
        </Box>
      )}

      <Typography sx={{ fontSize: '0.62rem', color: '#2a3c52', mt: 3, textAlign: 'center' }}>
        Trending data: Sleeper API (7-day adds) · NR: Nerd Rating · Proj: PPR pts estimate
      </Typography>
    </Box>
  );
}
